import React from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAbility } from "../Contexts/AbilityContext";
import { useSelector } from "react-redux";
import { Button } from "@fluentui/react-components";
import "./CSS/ProtectedRoute.css";

/**
 * ProtectedRoute Component
 * Vérifie si l'utilisateur a les permissions nécessaires pour accéder à une route
 * 
 * @param {string} module - Le nom du module (ex: "Queue", "Flow", "Agent", etc.)
 * @param {React.ReactNode} children - Le composant à rendre si l'accès est autorisé
 * @param {string} redirectTo - La route vers laquelle rediriger si l'accès est refusé (par défaut: "/")
 */
const ProtectedRoute = ({ module, children, redirectTo = "/" }) => {
  const { t } = useTranslation();
  const ability = useAbility();
  const SCOPES_NAMES = useSelector((state) => state.reducer.SCOPES_NAMES);
  const bootstrapMode = useSelector((state) => state.reducer.bootstrapMode);

  // Vérifie si l'utilisateur a au moins une permission (read/create/update/delete) sur main dans au moins un scope
  const hasAnyPermissionOnMain = () => {
    if (!ability || !SCOPES_NAMES || !module) {
      return false;
    }

    const actions = ["read", "create", "update", "delete"];
    return SCOPES_NAMES.some(scope => 
      actions.some(action => ability.can(action, `${module}.main@${scope}`))
    );
  };

  // Onboarding (tenant neuf, aucune permission) : on laisse passer la page de consent
  // (module "GlobalSettings" → couvre /globalSettings ET toutes les routes /Consent*),
  // sinon deadlock : impossible d'atteindre le 1er consent ni son callback Azure.
  if (bootstrapMode && module === "GlobalSettings") {
    return <>{children}</>;
  }

  const hasPermission = hasAnyPermissionOnMain();

  if (!hasPermission) {
    return (
      <div className="access-denied-container">
        <div className="access-denied-content">
          <h1 className="access-denied-title">{t('ACCESS_DENIED_TITLE')}</h1>
          <p className="access-denied-message">
            {t('ACCESS_DENIED_MESSAGE')}
          </p>
          <p className="access-denied-submessage">
            {t('ACCESS_DENIED_SUBMESSAGE')}
          </p>
          <Button
            appearance="primary"
            onClick={() => window.location.href = redirectTo}
          >
            {t('BACK_TO_HOME')}
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
